import { create } from "zustand";
import {
  IAudioInfo,
  IShareInfo,
  ESummaryType,
  ISummaryEditContent,
  BackTraceType,
  FormatBackTraceData,
  IFormatBackTraceArr,
  BackTraceData,
  IBackTrace,
  INewBackTraceIds,
} from "@/types/summary";
import {
  editSummaryContent,
  getAudioDownloadUrl,
  getAudioUrl,
  getBackTrace,
  getSrcDownloadUrl,
  getSummaryDownloadUrl,
  checkAuthRecordingFinished,
} from "@/services/summary";
import { getSpeechDraft, getSpeechDraftDownloadUrl } from "@/services/simulInter";
import { getShareAudioDownloadUrl, checkOpenRecordingFinished } from "@/services/share";
import summaryStore from "./summary.store";
import summaryChapterStore from "./summaryChapter.store";
import summarySpeakerStore from "./summarySpeaker.store";
import summarySrcStore from "./summarySrc.store";
import { AxiosRequestConfig } from "axios";
import { message } from "antd";
import { MeetingRecordVisibility, Origin } from "@/constants/share";
import { avatarBgs } from "@/pages/Voice/SpearkerAvatar/const";
import { defaultAvatarPrefix } from "@/utils/newAvatar";
import { subscribeWithSelector } from "zustand/middleware";
import formatOrigin from "@/utils/formatOrigin";
import { formatBackTraceData } from "@/bussiness/BackTraceHelper/util";
import { DetailErrorType, SummaryBackTraceArr, Tab } from "@/constants/record";
import { formatContent, formatTodo, updateContentBackTrace, updateTodoBackTrace, updateTodoBackTraceId } from "@/utils/backtrace";
import downloadFileWithUtf8Name from "@/utils/downloadFileWithUtf8Name";
import avPlayerStore from "./avPlayer.store";
import { IS_PRIVATE } from "@/config";

interface SummaryCommonState {
  // common
  audioInfo: IAudioInfo | null;
  videoLoadFailure: boolean;
  audioScTimeList: number[];
  shareInfo: IShareInfo | null;
  summaryEditing: boolean;
  summaryEditingFinish: boolean
  streamId: string;
  activeTab: Tab;
  avatarMap: Map<string, string>;
  fullLoading: boolean;
  retryStatus: 0 | 1 | 2 | 3; // 0: 未重试 1: 重试中 2: 重试成功 3: 重试失败
  errorStatus: DetailErrorType | null;
  isMobile: boolean;
  isFollowMode: boolean;
  isScrolled: boolean;
  origin: Origin;
  backTraceSearch: string;
  showBackTrace: boolean;
  showBackTraceEnter: boolean;
  backTraceData: FormatBackTraceData;
  backTracePollingCount: number;
  originBackTraceData: BackTraceData;
  backTraceArr: IFormatBackTraceArr[];
  activeBackTraceKey: string;
  newBackTraceIds: INewBackTraceIds;
  summaryKey: number;
  imagePreviewerVisible: boolean;
  abortPollingSignal: null | AbortController;
  abortRecordingPollingSignal: null | AbortController;
  showCompanyError: boolean;
  shareScope: MeetingRecordVisibility;
  isOnlyOriginal: boolean; // 文件是否仅原文
}

interface SummaryCommonStore extends SummaryCommonState {
  fetchAudioInfo: (
    param: { streamName: string },
    config?: AxiosRequestConfig<unknown>
  ) => void;
  refreshMediaInfo: () => void;
  initStore: () => void;
  setSummaryEditing: (editing: boolean) => void;
  setSummaryEditingFinish: (editing: boolean) => void;
  setActiveTab: (tab: Tab) => void;
  submitSummaryEdit: (reload?: boolean) => Promise<void>;
  setShareInfo: (shareInfo: IShareInfo | null) => void;
  setShareScope: (shareScope: MeetingRecordVisibility) => void;
  setStreamId: (streamId: string) => void;
  downloadAudio: (fileName: string) => Promise<void>;
  downloadSrcDoc: (fileName: string, contentStep: string) => Promise<void>;
  downloadSummary: (fileName: string, contentStep: string) => Promise<void>;
  downloadSpeechDraft: (fileName: string) => Promise<void>;
  checkSpeechDraftExists: () => Promise<boolean>;
  setFullLoading: (loading: boolean) => void;
  setRetryStatus: (status: 0 | 1 | 2 | 3) => void;
  getAvatar: (roleName: string) => string;
  clearAvatar: () => void;
  updateAvatarMap: (originRole: string, newRole: string) => void;
  setAudioScTimeList: (audioScTimeList: number[]) => void;
  setIsMobile: (isMobile: boolean) => void;
  setErrorStatus: (errCode: DetailErrorType | null) => void;
  setIsFollowMode: (isFollowMode: boolean) => void;
  setIsScrolled: (isScrolled: boolean) => void;
  setOrigin: (origin: Origin) => void;
  resetAudioInfo: () => void;
  setShowBackTrace: (showBackTrace: boolean) => void;
  setAbortPollingSignal: (abortPollingSignal: null | AbortController) => void;
  setAbortRecordingPollingSignal: (abortRecordingPollingSignal: null | AbortController) => void;
  setShowBackTraceEnter: (showBackTraceEnter: boolean) => void;
  updateBackTraceSearch: (type: BackTraceType, backTraceSearch: string, index: number) => void;
  fetchBackTraceData: (reload: boolean) => void;
  setActiveBackTraceKey: (activeBackTraceKey: string) => void;
  setOriginBackTraceData: (type: BackTraceType, newOriginBackTraceData: IBackTrace) => void;
  setNewBackTraceIds: (type: BackTraceType, newData: string[]) => void;
  pollingGetBackTrace: (backTrace: BackTraceType, params: {
    meetingSummaryEvent: string,
    reload: boolean
  }) => void;
  refreshSummaryKey: () => void;
  setImagePreviewerVisible: (visible: boolean) => void;
  setShowCompanyError: (showCompanyError: boolean) => void;
  setIsOnlyOriginal: (isOnlyOriginal: boolean) => void;
}

const summaryCommonState: SummaryCommonState = {
  audioInfo: null,
  videoLoadFailure: false,
  streamId: "",
  activeTab: Tab.summary,
  avatarMap: new Map(),
  retryStatus: 0,
  fullLoading: true,
  shareInfo: null,
  audioScTimeList: [],
  summaryEditing: false,
  summaryEditingFinish: false,
  isMobile: false,
  errorStatus: null,
  isFollowMode: true,
  isScrolled: false,
  origin: Origin.MAX_LINK,
  backTraceSearch: '',
  showBackTrace: false,
  backTraceData: {
    summaryBackTrace: {},
    contentBackTrace: {},
    todoBackTrace: {}
  },
  backTracePollingCount: 0,
  backTraceArr: [],
  showBackTraceEnter: false,
  originBackTraceData: {
    summaryBackTrace: {},
    contentBackTrace: {},
    todoBackTrace: {}
  },
  newBackTraceIds: {
    summaryBackTrace: [],
    contentBackTrace: [],
    todoBackTrace: []
  },
  activeBackTraceKey: '',
  summaryKey: 1,
  imagePreviewerVisible: false,
  abortPollingSignal: new AbortController(),
  abortRecordingPollingSignal: new AbortController(),
  showCompanyError: false,
  shareScope: MeetingRecordVisibility.all,
  isOnlyOriginal: false, // 默认非仅原文
};

const summaryCommonStore = create(
  subscribeWithSelector<SummaryCommonStore>((set, get) => ({
    ...summaryCommonState,
    initStore: () => {
      // 中断之前的轮询
      const oldPollingSignal = get().abortPollingSignal;
      const oldRecordingPollingSignal = get().abortRecordingPollingSignal;
      if (oldPollingSignal) {
        oldPollingSignal.abort();
      }
      if (oldRecordingPollingSignal) {
        oldRecordingPollingSignal.abort();
      }
      // 重置自己的 store 状态到初始值
      set({
        ...summaryCommonState,
        abortPollingSignal: new AbortController(),
        abortRecordingPollingSignal: new AbortController(),
      });
      // 初始化其他 store
      summaryStore.getState().initStore();
      summaryChapterStore.getState().initStore();
      summarySpeakerStore.getState().initStore();
      summarySrcStore.getState().initStore();
      avPlayerStore.getState().initStore();
    },
    fetchAudioInfo: async (params, config) => {
      set({
        videoLoadFailure: false,
      });
      // 抽取获取音频信息的公共方法
      const fetchAndSetAudioInfo = async (streamName: string, recordStatus: boolean | null): Promise<void> => {
        const shareInfo = get().shareInfo;
        let audioRes;

        if (shareInfo && get().shareScope !== MeetingRecordVisibility.orgMember) {
          audioRes = await getShareAudioDownloadUrl({ streamName, ...shareInfo }, config);
        } else {
          audioRes = await getAudioUrl({ streamName }, config);
        }

        if (!audioRes) {
          throw new Error("获取音频详情失败");
        }

        const streamId = get().streamId;
        if (!streamName.includes(streamId)) return;

        const { duration, pcmDownloadUrl, videoDownloadUrl, coverUrl } = audioRes;
        set({
          audioInfo: {
            audioDuration: duration,
            audioSrc: pcmDownloadUrl,
            videoSrc: videoDownloadUrl,
            coverUrl,
            recordStatus,
          },
        });
      };

      const checkAndFetchAudio = async (isFirstCall: boolean = true): Promise<void> => {
        const { streamName } = params;
        const shareInfo = get().shareInfo;
        // 检查录制状态
        let recordingCheckRes: {
          recordStatus: null | boolean, finishStatus: null | boolean
        } = { recordStatus: null, finishStatus: null };
        try {
          if (shareInfo && get().shareScope !== MeetingRecordVisibility.orgMember) {
            recordingCheckRes = await checkOpenRecordingFinished(streamName);
          } else {
            recordingCheckRes = await checkAuthRecordingFinished(streamName);
          }
        } catch (error: any) {
          console.error('checkRecordingFinished error', error);
          // 录制信息已删除
          if (error?.errCode === DetailErrorType.deleted) {
            get().setErrorStatus(DetailErrorType.deleted);
            return;
          }
          set({
            videoLoadFailure: true,
          });
        }

        const { recordStatus, finishStatus } = recordingCheckRes;
        try {
          // 如果recordStatus是false或null，直接获取音频URL
          if (recordStatus === false || recordStatus === null) {
            await fetchAndSetAudioInfo(streamName, recordStatus);
          } else {
            if (finishStatus === true) {
              await fetchAndSetAudioInfo(streamName, recordStatus);
            } else {
              const { audioInfo } = get();
              set({
                audioInfo: {
                  audioDuration: audioInfo?.audioDuration || 0,
                  audioSrc: audioInfo?.audioSrc || '',
                  recordStatus: recordStatus,
                },
              });
              // recordStatus为true但finishStatus为false时，第一次先获取音频信息，且无需await
              if (isFirstCall) fetchAndSetAudioInfo(streamName, recordStatus);

              // finishStatus为false表示正在生成链接中，每隔3s轮询一次
              const abortController = get().abortRecordingPollingSignal;
              if (abortController?.signal.aborted) return;
              setTimeout(() => {
                if (!abortController?.signal.aborted) checkAndFetchAudio(false);
              }, 3000);
            }
          }
        } catch (error: any) {
          console.log('fetchAudioInfo error', error);
          // 录制信息已删除
          if (error?.errCode === DetailErrorType.deleted) {
            get().setErrorStatus(DetailErrorType.deleted);
          }
        }
      };

      await checkAndFetchAudio();

    },
    refreshMediaInfo: async () => {
      await get().fetchAudioInfo(
        {
          streamName: `live/${get().streamId}`,
        },
      );
    },
    resetAudioInfo: () => {
      set({
        audioInfo: null,
        videoLoadFailure: false,
      });
    },
    setSummaryEditingFinish: (editing) => {
      set({ summaryEditingFinish: editing });
    },
    setSummaryEditing: (editing) => {
      const { initNewSummary } = summaryStore.getState();
      const { initNewChapterInfo } = summaryChapterStore.getState();
      const { initNewSpeakerSummary } = summarySpeakerStore.getState();

      if (editing) {
        initNewChapterInfo();
        initNewSpeakerSummary();
        initNewSummary();
        set({ summaryEditingFinish: false });
      }
      set({ summaryEditing: editing });
    },

    setActiveTab: (tab) => {
      set({ activeTab: tab });
    },
    submitSummaryEdit: async (reload = false) => {
      try {
        const currentActiveTab = get().activeTab;
        let newData: null | ISummaryEditContent = null;
        let summaryType: ESummaryType = ESummaryType.MEETING_SUMMARY;
        switch (currentActiveTab) {
          case Tab.summary: {
            const newSummary = summaryStore.getState().newSummary;
            if (!newSummary) {
              return;
            }
            // 将空段去除, 并重新整理回溯id
            let formatNewSummary = { ...newSummary };
            // 一定是先更新todoBackTraceId，再格式化todo
            updateTodoBackTraceId(formatNewSummary);
            formatNewSummary = formatTodo(formatNewSummary);
            // 更新contentBackTrace, 再格式化content
            formatNewSummary = formatContent(formatNewSummary);

            newData = {
              meetingSummary: {
                summary: formatNewSummary,
                summaryFinish: true,
              },
            };
            summaryType = ESummaryType.MEETING_SUMMARY;
            break;
          }
          case Tab.smartChapters: {
            newData = {
              smartChapters: {
                srcChapterList:
                  summaryChapterStore.getState().newChapterInfo!
                    .srcChapterList!,
              },
            };
            summaryType = ESummaryType.SMART_CHAPTERS;
            break;
          }
          case Tab.speakerSummary: {
            const newSpeakerSummary =
              summarySpeakerStore.getState().newSpeakerSummary;
            if (!newSpeakerSummary) {
              break;
            }
            newData = {
              meetingSpeakerSummary: {
                srcSpeakers: newSpeakerSummary,
              },
            };
            summaryType = ESummaryType.SPEAKER_SUMMARY;
            break;
          }
          default:
            break;
        }
        await editSummaryContent({
          streamName: `live/${get().streamId}`,
          summaryType,
          summaryInfo: { ...newData },
        });
        // 更改会议内容和待办观点回溯
        if (currentActiveTab === Tab.summary && !reload) {
          await updateTodoBackTrace(`live/${get().streamId}`);
          await updateContentBackTrace(`live/${get().streamId}`);
        }

        get().setSummaryEditingFinish(true);
        await summaryChapterStore.getState().fetchChapterInfo({
          streamName: `live/${get().streamId}`,
          reload: false,
          contentStep: "SRC_DOC",
          cover: true,
        }, true);
        await summaryStore.getState().fetchSummary({
          streamName: `live/${get().streamId}`,
          reload: false,
          contentStep: "SRC_DOC",
          cover: true,
        }, true);
        get().setSummaryEditing(false);
        await summarySpeakerStore.getState().fetchSpeakerSummary({
          streamName: `live/${get().streamId}`,
          reload: false,
          contentStep: "SRC_DOC",
          cover: true,
        }, true);
        // 重新总结后重新获取观点回溯
        get().fetchBackTraceData(reload);
        // TODO fetch new data
      } catch (error: any) {
        // 录制信息已删除
        if (error?.errCode === DetailErrorType.deleted) {
          get().setErrorStatus(DetailErrorType.deleted);
          return;
        }
        message.error("保存失败");
        console.error(error);
        // TODO 提示
      } finally {
        // TODO LOADING取消
      }
    },

    getAvatar: (roleName) => {
      const { avatarMap } = get();
      if (!avatarMap.has(roleName)) {
        const avatarIndex = avatarMap.size % avatarBgs.length;
        avatarMap.set(roleName, `${defaultAvatarPrefix}${avatarIndex}`);
      }
      return avatarMap.get(roleName) as string;
    },
    clearAvatar: () => {
      const { avatarMap } = get();
      avatarMap.clear();
    },
    setShareInfo: (shareInfo: IShareInfo | null) => {
      set({
        shareInfo: shareInfo ? { ...shareInfo, origin: formatOrigin(shareInfo?.origin) } as IShareInfo : null
      });
    },
    setStreamId: (streamId: string) => {
      set({ streamId });
    },
    downloadAudio: async (fileName) => {
      try {
        const { streamId } = get();
        if (!streamId) {
          return;
        }
        const res = await getAudioDownloadUrl({ streamId, fileName });
        if (!res?.data) {
          throw new Error("获取音频下载地址失败");
        }
        const downloadUrl = res.data as string;
        downloadFileWithUtf8Name(downloadUrl, fileName, '_音频');
      } catch (error: any) {
        console.log('downloadAudio error', error);
        // 录制信息已删除
        if (error.errCode === DetailErrorType.deleted) {
          get().setErrorStatus(DetailErrorType.deleted);
          return;
        }
        if (error.errCode === 40002144) {
          message.error(`权限发生变更,${error.errMsg}`);
          summarySrcStore.getState().updateDetailInfo();
          return;
        }
        if (error.errCode === 40002104) {
          message.error(`权限发生变更,${error.errMsg}`);
          window.navigate(`/SummaryPermissionError?streamId=${get().streamId}`, {
            replace: true,
          });
          return;
        }
      }
    },
    downloadSrcDoc: async (fileName, contentStep) => {
      try {
        const { streamId } = get();
        if (!streamId) {
          return;
        }
        const res = await getSrcDownloadUrl({
          streamId,
          fileName,
          contentStep,
        });
        if (!res?.data) {
          throw new Error("获取原文下载地址失败");
        }
        const downloadUrl = res.data as string;
        downloadFileWithUtf8Name(downloadUrl, fileName, '_原文');
      } catch (error: any) {
        console.log('downloadSrcDoc error', error);
        // 录制信息已删除
        if (error.errCode === DetailErrorType.deleted) {
          get().setErrorStatus(DetailErrorType.deleted);
          return;
        }
        if (error.errCode === 40002144) {
          message.error(`权限发生变更,${error.errMsg}`);
          summarySrcStore.getState().updateDetailInfo();
          return;
        }
        if (error.errCode === 40002104) {
          message.error(`权限发生变更,${error.errMsg}`);
          window.navigate(`/SummaryPermissionError?streamId=${get().streamId}`, {
            replace: true,
          });
          return;
        }
      }
    },
    downloadSummary: async (fileName, contentStep) => {
      try {
        const { streamId } = get();
        if (!streamId) {
          return;
        }
        const res = await getSummaryDownloadUrl({
          streamId,
          fileName,
          contentStep,
        });
        if (!res?.data) {
          throw new Error("获取概要下载地址失败");
        }
        const downloadUrl = res.data as string;
        // 因为私有化的环境 cstore 对 filename 的转义有问题
        if (IS_PRIVATE) {
          downloadFileWithUtf8Name(downloadUrl, fileName, '_智能总结');
        } else {
          const a = document.createElement("a");
          a.href = downloadUrl;
          a.click();
        }
      } catch (error: any) {
        console.log('downloadSummary error', error);
        // 录制信息已删除
        if (error.errCode === DetailErrorType.deleted) {
          get().setErrorStatus(DetailErrorType.deleted);
          return;
        }
        if (error.errCode === 40002144) {
          message.error(`权限发生变更,${error.errMsg}`);
          summarySrcStore.getState().updateDetailInfo();
          return;
        }
        if (error.errCode === 40002104) {
          message.error(`权限发生变更,${error.errMsg}`);
          window.navigate(`/SummaryPermissionError?streamId=${get().streamId}`, {
            replace: true,
          });
          return;
        }
      }
    },
    // 检查发言稿是否存在
    checkSpeechDraftExists: async () => {
      try {
        const { streamId } = get();
        if (!streamId) {
          return false;
        }
        const streamName = `live/${streamId}`;
        // return true;
        const res = await getSpeechDraft(streamName, '发言稿.docx');
        return !!res.data;
      } catch (error) {
        console.log('checkSpeechDraftExists error', error);
        return false;
      }
    },
    // 下载发言稿
    downloadSpeechDraft: async (fileName) => {
      try {
        const { streamId } = get();
        if (!streamId) {
          return;
        }
        // const streamId = '856dc76ec0fb43e1b6d696714f9be9f4'
        const streamName = `live/${streamId}`;
        const res = await getSpeechDraftDownloadUrl({ streamName });
        if (!res?.data) {
          throw new Error("获取发言稿下载地址失败");
        }
        const downloadUrl = res.data as string;
        downloadFileWithUtf8Name(downloadUrl, fileName, '_发言稿');
      } catch (error: any) {
        console.log('downloadSpeechDraft error', error);
        message.error('发言稿下载失败，请重试');
      }
    },

    setFullLoading: (loading) => {
      set({ fullLoading: loading });
    },

    setErrorStatus: (errCode) => {
      set({ errorStatus: errCode });
    },

    setRetryStatus: (status) => {
      set({ retryStatus: status });
    },
    setAudioScTimeList: (audioScTimeList) => {
      set({ audioScTimeList });
    },
    setIsMobile: (isMobile: boolean) => {
      set({ isMobile });
    },
    updateAvatarMap: (originRole, newRole) => {
      const { avatarMap, getAvatar } = get();
      if (avatarMap.has(newRole)) {
        return;
      } else {
        const originAvatar = getAvatar(originRole);
        avatarMap.set(newRole, originAvatar);
        avatarMap.delete(originRole);
      }
    },
    setIsFollowMode: (isFollowMode) => {
      set({ isFollowMode });
    },
    setIsScrolled: (isScrolled) => {
      set({ isScrolled });
    },
    setOrigin: (origin: Origin) => {
      set({ origin: formatOrigin(origin) });
    },
    pollingGetBackTrace: async (backTrace: BackTraceType, params: {
      meetingSummaryEvent: string,
      reload: boolean
    }) => {
      try {
        const { meetingSummaryEvent, reload } = params;
        const res = await getBackTrace(`live/${get().streamId}`, meetingSummaryEvent, reload, { signal: get().abortPollingSignal?.signal });
        get().setOriginBackTraceData(backTrace, res.data[backTrace]);
        set({
          showBackTraceEnter: true
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        console.log('pollingGetBackTrace error', error, get().abortPollingSignal);
        if (!get().abortPollingSignal) return;
        // 总结内容不存在，无法进行观点回溯, 再次重试
        if (error.errCode === 40002136) {
          const currPollingCount = get().backTracePollingCount;
          // 观点回溯最多请求10次*3s
          if (currPollingCount >= 10) {
            set({ backTracePollingCount: 0 });
          } else {
            set({ backTracePollingCount: currPollingCount + 1 });
            setTimeout(() => {
              get().pollingGetBackTrace(backTrace, params);
            }, 3000);
          }
        }
      }
    },
    fetchBackTraceData: async (reload: boolean) => {
      try {
        const shareInfo = get().shareInfo;
        if (shareInfo) return;
        Object.keys(SummaryBackTraceArr).forEach(async backTrace => {
          const meetingEventType = SummaryBackTraceArr[backTrace];
          get().pollingGetBackTrace(backTrace as BackTraceType, {
            meetingSummaryEvent: meetingEventType,
            reload
          });
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      catch (error: any) {
        console.log('fetchBackTraceData error', error);
      }
    },
    setShowBackTrace: (showBackTrace: boolean) => {
      set({ showBackTrace });
    },
    setAbortPollingSignal: (abortPollingSignal) => {
      set({ abortPollingSignal });
    },
    setAbortRecordingPollingSignal: (abortRecordingPollingSignal) => {
      set({ abortRecordingPollingSignal });
    },
    setShowBackTraceEnter: (showBackTraceEnter: boolean) => {
      set({ showBackTraceEnter });
    },
    updateBackTraceSearch: (type: BackTraceType, backTraceSearch: string, index: number) => {
      const backTraceArr = get().backTraceData[type][index] as IFormatBackTraceArr[];
      set({
        showBackTrace: true,
        backTraceSearch,
        backTraceArr: backTraceArr || []
      });
    },
    setActiveBackTraceKey: (activeBackTraceKey: string) => {
      set({ activeBackTraceKey });
    },
    setOriginBackTraceData: (key, originNewData) => {
      const { originBackTraceData, backTraceData } = get();
      const formatNewData = formatBackTraceData(originNewData);
      if (!formatNewData) {
        return;
      }
      Object.assign(backTraceData, { [key]: formatNewData });
      Object.assign(originBackTraceData, { [key]: originNewData });
      set({ originBackTraceData: { ...originBackTraceData }, backTraceData: { ...backTraceData } });
    },
    setNewBackTraceIds: (key, newData) => {
      const { newBackTraceIds } = get();
      Object.assign(newBackTraceIds, { [key]: newData });
      set({ newBackTraceIds: { ...newBackTraceIds } });
    },
    refreshSummaryKey: () => {
      set({ summaryKey: get().summaryKey + 1 });
    },
    setImagePreviewerVisible: (visible: boolean) => {
      set({ imagePreviewerVisible: visible });
    },
    setShowCompanyError: (showCompanyError: boolean) => {
      set({ showCompanyError });
    },
    setShareScope: (shareScope: MeetingRecordVisibility) => {
      set({ shareScope });
    },
    setIsOnlyOriginal: (isOnlyOriginal: boolean) => {
      set({ isOnlyOriginal });
    }
  }))
);

export default summaryCommonStore;
